import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";

import { AuthShell } from "@/features/auth/modules/auth-shell/auth-shell";
import { SignupForm } from "@/features/auth/modules/signup/signup-form";
import { joinPathOf } from "@/features/auth/utils/invite-code";
import { pageMeta } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";

export const Route = createFileRoute("/(public)/signup/")({
  // Nobody searches for a form: the page people want in search is the one that leads here.
  staticData: { sitemap: false },
  head: () => ({ meta: pageMeta({ title: m.seo_sign_up_title() }) }),
  component: RouteComponent,
  loader: () => {
    return {
      crumb: m.auth_signup_crumb(),
    };
  },
  validateSearch: z.object({
    redirect: z.string().optional(),
    /** A trainer's invite code, from the link in their invite email. */
    code: z.string().max(32).optional(),
  }),
});

function RouteComponent() {
  const { redirect, code } = Route.useSearch();

  // Same hand-off the login page does: a visitor who picked a time and then chose to create an
  // account still lands on that time once they are in.
  useEffect(() => {
    if (redirect) {
      sessionStorage.setItem("auth_redirect_path", redirect);
    }
  }, [redirect]);

  return (
    <AuthShell
      eyebrow={m.auth_signup_eyebrow()}
      title={{ lead: m.auth_signup_title_lead(), accent: m.auth_signup_title_accent() }}
      description={m.auth_signup_description()}
      aside={{
        prompt: m.auth_signup_aside_prompt(),
        label: m.auth_signup_aside_action(),
        to: "/login",
      }}
      // Someone with an invite who turns out to have an account already signs in instead, and
      // lands on the join page with their code rather than losing it.
      redirect={redirect ?? (code ? joinPathOf(code) : undefined)}
    >
      <SignupForm />
    </AuthShell>
  );
}
